/** @jsx jsx */
import { css, jsx } from '@emotion/core';

import {
  Currency,
  defaultCurrency,
} from '@zapier/currency/src/hooks/useCurrencies';
import {
  formatPrice,
  formatPriceToBeInCurrency,
} from '@zapier/currency/src/utils/formatPriceToBeInCurrency';

type PriceProps = {
  /** Price in USD pennies (i.e. hundreths of a USD dollar) */
  children: number;
  /** Currency to show price in */
  currency?: Currency;
  /** Hide currency symbol */
  hideSymbol?: boolean;
  /** Use rounding relative to first digit */
  isFriendly?: boolean;
  /** Max number of digits to show */
  maxDecimals?: number;
  /** Minimum number of decimals to show */
  minDecimals?: number;
  /** Applies font size to currency symbol only */
  symbolFontSize?: string;
  /** Set to true if we're passing a localized price that requires no calculation */
  isLocalized?: boolean;
};

const priceCss = css({
  alignItems: 'center',
  display: 'inline-flex',
});

/**
 * Displays a price in a user-friendly format
 */
export const Price = ({
  children,
  currency = defaultCurrency,
  hideSymbol = false,
  isFriendly = false,
  maxDecimals = 2,
  minDecimals = 0,
  symbolFontSize = 'inherit',
  isLocalized = false,
}: PriceProps) => {
  const absPrice = Math.abs(children);
  const isNegative = children < 0;

  const formattingOptions = {
    maxDecimals,
    minDecimals,
    useRelativeRounding: isFriendly,
  };

  const price = isLocalized
    ? formatPrice(absPrice, formattingOptions)
    : formatPriceToBeInCurrency(absPrice, currency, false, formattingOptions);

  return (
    <span css={priceCss}>
      {isNegative && <span>-</span>}
      {!hideSymbol && (
        <span
          style={{
            fontSize: symbolFontSize,
          }}
        >
          {currency.symbol}
        </span>
      )}
      <span>{price}</span>
      {currency.symbol === '$' && <span>&nbsp;{currency.name}</span>}
    </span>
  );
};
